import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../Components/Firebase";
import { Card, Row, Col, Container, Badge, Button } from "react-bootstrap";

const AniSparkPlayer = ({ videoId }) => (
  <iframe
    title="AniSpark Video Player"
    src={`https://iframe.videodelivery.net/${videoId}`}
    style={{ 
      width: '100%', 
      aspectRatio: '16/9', 
      border: 'none',
      borderRadius: '8px'
    }}
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
);

const CreatorProfile = () => {
  const { creatorId } = useParams();
  const [videos, setVideos] = useState([]);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalViews: 0,
    totalEpisodes: 0,
    genres: []
  });

  useEffect(() => {
    const fetchCreatorAndVideos = async () => {
      try {
        // Fetch creator data
        const creatorRef = doc(db, "creators", creatorId);
        const creatorSnapshot = await getDoc(creatorRef);
        
        if (creatorSnapshot.exists()) {
          setCreator({ id: creatorSnapshot.id, ...creatorSnapshot.data() });
        }

        // Fetch episodes
        const q = query(
          collection(db, "episodes"),
          where("creatorId", "==", creatorId)
        );
        const querySnapshot = await getDocs(q);
        const episodes = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setVideos(episodes);

        // Calculate stats
        const totalViews = episodes.reduce((sum, episode) => sum + (episode.views || 0), 0);
        const uniqueGenres = [...new Set(episodes.map(episode => episode.genre))];
        
        setStats({
          totalViews,
          totalEpisodes: episodes.length,
          genres: uniqueGenres
        });

      } catch (error) {
        console.error("Error fetching creator data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreatorAndVideos();
  }, [creatorId]);

  if (loading) {
    return (
      <div className="explore-page" style={{ minHeight: "100vh" }}>
        <div className="star-field">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        <Container className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <div className="text-light text-center">
            <div className="spinner-border text-light mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading creator profile...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="explore-page" style={{ minHeight: "100vh" }}>
        <div className="star-field">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        <Container className="py-5 text-center">
          <div className="text-light">
            <h2>Creator Not Found</h2>
            <p>The creator you're looking for doesn't exist or has been removed.</p>
            <Link to="/explore">
              <Button variant="primary">Back to Explore</Button>
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="explore-page" style={{ minHeight: "100vh" }}>
      <div className="star-field">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            {/* Creator Header */}
            <Card 
              className="mb-5"
              style={{ 
                backgroundColor: "#1e1b4b", 
                border: "1px solid #374151",
                borderRadius: "16px",
                overflow: 'hidden'
              }}
            >
              <div 
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  height: '120px',
                  position: 'relative'
                }}
              >
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-50px',
                    left: '30px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    border: '4px solid #1e1b4b',
                    background: creator.profileImageUrl 
                      ? `url(${creator.profileImageUrl})` 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                >
                  {!creator.profileImageUrl && creator.username ? creator.username.charAt(0).toUpperCase() : ''}
                </div>
              </div>
              
              <Card.Body className="pt-5 pb-4" style={{ paddingLeft: '30px', paddingRight: '30px' }}>
                <Row className="align-items-center">
                  <Col md={8} style={{ paddingLeft: '120px' }}>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <h1 className="text-light mb-0" style={{ fontSize: '1.8rem', fontWeight: '700' }}>
                        {creator.username || 'Creator'}
                      </h1>
                      {creator.founderBadge && (
                        <Badge 
                          bg="warning" 
                          text="dark" 
                          className="px-3 py-1"
                          style={{ 
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            borderRadius: '20px'
                          }}
                        >
                          ✨ Founding Creator
                        </Badge>
                      )}
                    </div>
                    
                    {creator.bio && (
                      <p className="text-light mb-3" style={{ fontSize: '1rem', opacity: '0.9', lineHeight: '1.5' }}>
                        {creator.bio}
                      </p>
                    )}
                    
                    <div className="d-flex align-items-center gap-4 flex-wrap">
                      <div className="text-light">
                        <span className="fw-bold" style={{ fontSize: '1.1rem' }}>
                          {stats.totalEpisodes}
                        </span>
                        <span className="text-muted ms-1">Episodes</span>
                      </div>
                      <div className="text-light">
                        <span className="fw-bold" style={{ fontSize: '1.1rem' }}>
                          {stats.totalViews.toLocaleString()}
                        </span>
                        <span className="text-muted ms-1">Total Views</span>
                      </div>
                      <div className="text-light">
                        <span className="fw-bold" style={{ fontSize: '1.1rem' }}>
                          {stats.genres.length}
                        </span>
                        <span className="text-muted ms-1">Genre{stats.genres.length !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={4} className="text-md-end">
                    {creator.patreonUrl && (
                      <div className="d-flex flex-column gap-2">
                        <Button
                          href={creator.patreonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-patreon d-flex align-items-center justify-content-center gap-2"
                          style={{
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '12px 24px',
                            fontWeight: '600',
                            fontSize: '1rem',
                            boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.3)';
                          }}
                        >
                          <span style={{ fontSize: '1.2rem' }}>💖</span>
                          Support Creator
                        </Button>
                        <small className="text-muted text-center">
                          Help support amazing content!
                        </small>
                      </div>
                    )}
                    
                    <Link to="/explore" className="text-decoration-none">
                      <Button 
                        variant="outline-light" 
                        className="d-flex align-items-center gap-2 mt-3"
                        style={{ borderRadius: '8px', padding: '8px 16px' }}
                      >
                        <span>←</span>
                        Back to Explore
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Genres Section */}
            {stats.genres.length > 0 && (
              <Card 
                className="mb-4"
                style={{ 
                  backgroundColor: "#1e1b4b", 
                  border: "1px solid #374151",
                  borderRadius: "12px"
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="text-light mb-3">Genres</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {stats.genres.map((genre, index) => (
                      <Badge 
                        key={index}
                        bg="info" 
                        className="px-3 py-2"
                        style={{ 
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          borderRadius: '20px'
                        }}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Episodes Section */}
            <div className="mb-4">
              <h2 className="text-light mb-4" style={{ fontSize: '1.8rem', fontWeight: '700' }}>
                Episodes ({videos.length})
              </h2>
            </div>

            {videos.length === 0 ? (
              <Card 
                style={{ 
                  backgroundColor: "#1e1b4b", 
                  border: "1px solid #374151",
                  borderRadius: "12px"
                }}
              >
                <Card.Body className="p-5 text-center">
                  <div className="text-light">
                    <div style={{ fontSize: '3rem', marginBottom: '20px', opacity: '0.5' }}>📺</div>
                    <h4 className="mb-3">No episodes yet</h4>
                    <p className="text-muted">
                      This creator hasn't uploaded any episodes yet. Check back later!
                    </p>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-4">
                {videos.map((video) => (
                  <Col key={video.id} lg={4} md={6}>
                    <Card 
                      className="mb-4 episode-card" 
                      style={{ 
                        backgroundColor: "#1e1b4b", 
                        border: "1px solid #374151",
                        borderRadius: "12px",
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      }}
                    >
                      <AniSparkPlayer videoId={video.videoId} />
                      
                      <Card.Body className="p-4">
                        <div className="mb-3">
                          <h5 className="text-light mb-2" style={{ fontSize: '1.1rem', fontWeight: '600', lineHeight: '1.3' }}>
                            {video.title}
                          </h5>
                          
                          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                            <Badge 
                              bg="info" 
                              className="px-2 py-1"
                              style={{ 
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                borderRadius: '16px'
                              }}
                            >
                              {video.genre}
                            </Badge>
                            {video.trending && (
                              <Badge 
                                bg="danger" 
                                className="px-2 py-1"
                                style={{ 
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  borderRadius: '16px',
                                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                                  border: 'none'
                                }}
                              >
                                🔥 Trending
                              </Badge>
                            )}
                            {video.nsfw && (
                              <Badge 
                                bg="warning" 
                                text="dark" 
                                className="px-2 py-1"
                                style={{ 
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  borderRadius: '16px'
                                }}
                              >
                                NSFW
                              </Badge>
                            )}
                          </div>
                          
                          <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: '0.85rem' }}>
                            <span className="text-light" style={{ fontWeight: '500' }}>
                              {(video.views || 0).toLocaleString()} views
                            </span>
                            <span>•</span>
                            <span>
                              {video.date?.toDate().toLocaleDateString()}
                            </span>
                          </div>
                          
                          {video.nsfwReason && (
                            <div className="text-muted small mt-2">
                              ⚠️ {video.nsfwReason}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-center">
                          <Link 
                            to={`/watch/${video.id}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Button
                              className="w-100"
                              style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                              }}
                            >
                              Watch Episode
                            </Button>
                          </Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .episode-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.4) !important;
        }
        
        .episode-card {
          transition: all 0.3s ease;
        }
        
        .btn-patreon:hover {
          transform: translateY(-2px) !important;
        }
        
        .star {
          position: absolute;
          width: 2px;
          height: 2px;
          background: white;
          border-radius: 50%;
          animation: twinkle 3s ease-in-out infinite;
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        .explore-page {
          background: linear-gradient(135deg, #1e1b4b 0%, #3c366b 100%);
          position: relative;
          overflow: hidden;
        }
        
        .star-field {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        
        .container-fluid {
          position: relative;
          z-index: 2;
        }
      `}</style>
    </div>
  );
};

export default CreatorProfile;